import { useEffect, useState } from 'react'
import { listConversationCapabilities } from '@/api/conversation'

const noPhrases: Array<string> = []

/**
 * The suggestion corpus for one workspace: every phrase the granted capabilities can offer
 * here, flattened in the order the server composed them.
 *
 * It is read once per workspace rather than per keystroke. The composer matches locally
 * over a corpus the size of the design reference's, so a request per character would buy
 * nothing and make narrowing depend on the network. Call it above anything keyed to the
 * open chat: the corpus depends on the workspace alone, and a hook mounted per chat reads
 * the same list again on every switch.
 *
 * A failed read narrows to nothing. Narrowing is advisory: a composer that cannot suggest
 * is still a composer, and free text is first-class either way.
 */
export function useConversationSuggestions(projectId: number): Array<string> {
  // The workspace the phrases belong to is held with them, so a render that changes
  // workspace shows nothing rather than the previous workspace's material for the frame
  // before the effect clears it. Effects run after paint; this does not.
  const [corpus, setCorpus] = useState<{ projectId: number; phrases: Array<string> }>({
    projectId,
    phrases: noPhrases
  })

  useEffect(() => {
    const controller = new AbortController()
    listConversationCapabilities(projectId, { signal: controller.signal })
      .then((capabilities) => {
        if (controller.signal.aborted) return
        setCorpus({ projectId, phrases: capabilities.flatMap((capability) => capability.suggestions) })
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setCorpus({ projectId, phrases: noPhrases })
      })
    return () => controller.abort()
  }, [projectId])

  return corpus.projectId === projectId ? corpus.phrases : noPhrases
}
